import { memo, useCallback, useEffect, useState } from 'react'
import styles from './index.module.scss'
import { Button, Modal } from 'antd'
import { PositionsTable } from '../../components/positions-table'
import { Position } from 'types/positions'
import { createPosition, deletePosition, editPosition, getPositionById, getPositions } from 'services/positions'
import { PositionForm } from '../../components/position-form'

export const Positions = memo(() => {
    const [positions, setPositions] = useState<Position[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [position, setPosition] = useState<Position>({} as Position)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getPositions()
        }

        getAll()
            .then(data => {
                setPositions(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setPosition({} as Position)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getPositionById(id)
                .then(item => {
                    setPosition(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deletePosition(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: Position) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (position.id) {
                editPosition({
                    ...submitValues,
                    id: position.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createPosition(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, position, toggleModal]
    )

    const editMode = !!position.id
    const modalTitle = `${editMode ? `Edit position ${position.name}` : `Add new position`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Positions
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <PositionsTable data={positions} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <PositionForm position={position} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})
